import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { Button, ButtonGroup, Filter, sendHoverSoundCommand, sendClickSoundCommand } from 'react-wot-bootstrap'
import SOUNDS from 'react-wot-bootstrap/lib/sounds'

import { Back } from 'components/Back/Back'
import { useCallbackOnEsc } from 'hooks/useCallbackOnEsc'
import Language from './components/Language/Language'
import Levels from './components/Levels/Levels'
import { useMetricsLog } from 'helpers/uilogging/hooks'
import { Actions, FEATURE, Items } from 'containers/Landing/logConstants'

import {
    getLocalizedLanguageTitle,
    normalizeBackCnLangForServer,
    normalizeLanguageCodeForClient
} from 'state/actions/helpers/recommended_clans'
import {
    fetchRecommendedClans,
    setLanguageCode,
    setMembersCount,
    setListType,
    sendFilterSettings,
} from 'state/actions/recommended_clans'

import {
    REALM_LANGUAGE_CODES,
    FILTERS_TITLE,
    FILTERS_PLAYERS,
    FILTERS_LANGUAGE,
    FILTERS_ACTIVITY,
    FILTERS_SAVE,
    FILTERS_CLOSE,
    MEMBERS_COUNT_ITEMS,
} from './constants.js'

import styles from './Filters.scss'

const Filters = ({ isFull, isOpened, onClose, partnerID }) => {
    const log = useMetricsLog(FEATURE)
    const dispatch = useDispatch()
    const { realm } = useSelector((state) => state.settings)
    const { languages, membersCount, listType, dwhType, total } = useSelector((state) => state.recommendedClans)

    const [langugesFilter, setLangugesFilter] = useState(languages)
    const [membersFilter, setMembersFilter] = useState(membersCount)
    const [listFilter, setListFilter] = useState(listType)
    const [hasChanges, setHasChanges] = useState(false)

    useEffect(() => {
        if (!membersCount) {
            dispatch(setMembersCount(1))
        }
    }, [])

    const handleButtonClick = useCallback(() => {
        log({
            action: Actions.Click,
            item: Items.SaveButton,
            parentScreen: Items.LandingClans,
            partnerID: partnerID,
            info: JSON.stringify({
                type: dwhType,
                total: total,
                membersCount: membersCount,
                gameLevel: listType,
                languages: languages,
            }),
        })
    }, [log])

    useEffect(() => {
        if (!hasChanges) {
            setLangugesFilter(languages)
            setMembersFilter(membersCount)
            setListFilter(listType)
        }
    }, [hasChanges, languages, membersCount, listFilter])

    const selectLanguage = useCallback(
        (lang) => {
            setHasChanges(true)
            if (languages.includes(lang)) {
                dispatch(setLanguageCode(languages.filter((item) => item !== lang)))
            } else {
                dispatch(setLanguageCode([...languages, lang]))
            }
        },
        [languages]
    )

    const selectListType = useCallback((listType) => {
        setHasChanges(true)
        dispatch(setListType(listType))
    }, [])

    const handleSetPLayers = useCallback((membersCount) => {
        setHasChanges(true)
        dispatch(setMembersCount(membersCount))
    }, [])

    const handleSave = useCallback(() => {
        handleButtonClick()
        if (hasChanges) {
            dispatch(sendFilterSettings())
            dispatch(fetchRecommendedClans())
        }
        onClose(true)
        setHasChanges(false)
    }, [membersCount, listType, languages, onClose, hasChanges, langugesFilter])

    const handleClose = useCallback(() => {
        dispatch(setLanguageCode(langugesFilter))
        dispatch(setMembersCount(membersFilter))
        dispatch(setListType(listFilter))
        onClose(false)
        setHasChanges(false)
    }, [onClose])

    const selectedMembersCountItem = MEMBERS_COUNT_ITEMS.find((item) => item.value === membersCount)
    const shouldHideLanguageSection = realm === 'cn360' && REALM_LANGUAGE_CODES[realm].length === 1

    useCallbackOnEsc((e, nextEscCallback) => {
        if (isOpened) {
            handleClose()
            return
        }

        nextEscCallback && nextEscCallback()
    })

    return (
        <div className={cx(styles.base, isOpened && styles.base__opened)}>
            <div className={styles.background} />
            <div className={styles.back}>
                <Back
                    caption={Back.CAPTIONS.close}
                    type={Back.TYPES.close}
                    side={Back.SIDES.right}
                    handleClick={handleClose}
                    playHoverSound={sendHoverSoundCommand}
                    playClickSound={sendClickSoundCommand}
                />
            </div>
            <div className={styles.container}>
                <div className={styles.title}>{FILTERS_TITLE}</div>
                {isFull && (
                    <div className={styles.section}>
                        <div className={styles.subtitle}>{FILTERS_ACTIVITY}</div>
                        <Levels level={listType} onClick={selectListType} />
                    </div>
                )}
                <div className={cx(styles.section, isFull && styles.section__bordered)}>
                    <div className={styles.subtitle}>{FILTERS_PLAYERS}</div>
                    <Filter
                        items={MEMBERS_COUNT_ITEMS}
                        checkedItem={
                            (selectedMembersCountItem && selectedMembersCountItem.value) || MEMBERS_COUNT_ITEMS[0].value
                        }
                        onItemChange={handleSetPLayers}
                    />
                </div>
                {!shouldHideLanguageSection && (
                    <div className={cx(styles.section, styles.section__bordered)}>
                        <div className={styles.subtitle}>{FILTERS_LANGUAGE}</div>
                        <div className={styles.languages}>
                            {REALM_LANGUAGE_CODES[realm] &&
                                REALM_LANGUAGE_CODES[realm].map((lang) => {
                                    const stateLangCode = normalizeBackCnLangForServer(lang, realm)
                                    return (
                                        <Language
                                            key={lang}
                                            code={normalizeLanguageCodeForClient(lang, realm)}
                                            title={getLocalizedLanguageTitle(lang, realm)}
                                            onClick={() => selectLanguage(stateLangCode)}
                                            isChecked={languages.includes(stateLangCode)}
                                        />
                                    )
                                })}
                        </div>
                    </div>
                )}
                <div className={styles.buttons}>
                    <ButtonGroup>
                        <Button
                            mixClassName={styles.button}
                            isJustified
                            onClick={handleSave}
                        >
                            {FILTERS_SAVE}
                        </Button>
                        <Button
                            mixClassName={styles.button}
                            isJustified
                            onClick={handleClose}
                            type="secondary"
                            buttonClickSoundId={SOUNDS.CLICK}
                        >
                            {FILTERS_CLOSE}
                        </Button>
                    </ButtonGroup>
                </div>
            </div>
        </div>
    )
}

Filters.propTypes = {
    isOpened: PropTypes.bool.isRequired,
    isFull: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    partnerID: PropTypes.string.isRequired,
}

export default Filters



// WEBPACK FOOTER //
// ./containers/Landing/components/LandingClans/components/Filters/Filters.jsx